/**
 * Utility function for converting ThreadSearchResult to ChatThread
 * Used when displaying search results in the thread list
 */

import {
  resolveContactName,
  UNKNOWN_CONTACT_NAME,
  type ThreadSearchResult,
  type SenderType,
} from '@super/shared-types'
import type { ChatThread } from '../types'

/**
 * Converts a ThreadSearchResult from the search API response to a ChatThread
 * for display in the thread list UI.
 *
 * @param thread - The thread search result from the search API's included.threads
 * @param compositeId - A unique ID combining threadId and sessionId for deduplication
 * @param sessionId - The session ID for this search result
 * @returns A ChatThread suitable for display in the thread list
 */
export function threadSearchResultToChatThread(
  thread: ThreadSearchResult,
  compositeId: string,
  sessionId: string,
): ChatThread {
  const contactName = thread.contact
    ? resolveContactName(thread.contact)
    : UNKNOWN_CONTACT_NAME

  const senderType: SenderType =
    contactName === UNKNOWN_CONTACT_NAME ? 'phone' : 'contact'

  return {
    id: compositeId,
    contactId: thread.contactId,
    companyId: thread.companyId,
    threadType: thread.threadType,
    userId: thread.contactId,
    sessionId,
    createdAt: new Date(thread.createdAt),
    updatedAt: new Date(thread.updatedAt),
    company: {
      id: thread.companyId,
      name: 'Company',
      avatar: undefined,
    },
    participant: {
      id: thread.contactId,
      name: contactName,
      senderType,
      email: thread.email || undefined,
      phone: thread.phone || undefined,
      avatar: undefined,
    },
    lastThreadEvent: undefined,
    unreadCount: 0,
  }
}
